/**
 * Service Breakdown Chart Component
 *
 * Pie chart showing the distribution of costs across AWS services.
 */

import { useMemo } from 'react';
import { Pie, PieChart, Cell } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatCurrency } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import type { CostByService } from '@/types/dashboard';

interface ServiceBreakdownChartProps {
  data: CostByService[] | undefined;
  isLoading: boolean;
}

// Color palette for services
const SERVICE_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'hsl(240, 60%, 50%)',
  'hsl(180, 60%, 50%)',
  'hsl(120, 60%, 50%)',
];

// Shorten verbose AWS service names for display
const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  'AWS Lambda': 'Lambda',
  'Amazon DynamoDB': 'DynamoDB',
  'Amazon Simple Storage Service': 'S3',
  'Amazon S3': 'S3',
  'Amazon Bedrock': 'Bedrock',
  'Amazon CloudWatch': 'CloudWatch',
  'Amazon API Gateway': 'API Gateway',
  'AWS Secrets Manager': 'Secrets Manager',
  'Amazon Cognito': 'Cognito',
  'AWS Key Management Service': 'KMS',
  'Amazon CloudFront': 'CloudFront',
};

function getServiceDisplayName(service: string): string {
  return SERVICE_DISPLAY_NAMES[service] || service;
}

export function ServiceBreakdownChart({
  data,
  isLoading,
}: ServiceBreakdownChartProps) {
  // Transform data for pie chart
  const { chartData, chartConfig } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], chartConfig: {} as ChartConfig };
    }

    const chartData = data.map((item, i) => ({
      name: getServiceDisplayName(item.service),
      value: item.cost,
      percentage: item.percentage,
      fill: SERVICE_COLORS[i % SERVICE_COLORS.length],
    }));

    // Build config for legend
    const config: ChartConfig = {};
    data.forEach((item, i) => {
      config[item.service] = {
        label: getServiceDisplayName(item.service),
        color: SERVICE_COLORS[i % SERVICE_COLORS.length],
      };
    });

    return { chartData, chartConfig: config };
  }, [data]);

  const showSkeleton = isLoading || data === undefined;
  const isEmpty = !showSkeleton && (!data || data.length === 0);

  return (
    <ChartCard
      title="Cost by Service"
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No cost data available"
    >
      <ChartContainer config={chartConfig} className={CHART_HEIGHT_CLASSES}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            innerRadius={60}
            paddingAngle={2}
            label={({ name, payload }) =>
              `${name}: ${(payload as { percentage: number }).percentage}%`
            }
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) =>
                  `${name}: ${formatCurrency(Number(value))}`
                }
              />
            }
          />
        </PieChart>
      </ChartContainer>
    </ChartCard>
  );
}
